import type { LearnerState } from '@/types/learner';
import { TOTAL_MODULES } from '@/seed/modules';
import { PERSONA_ORDER } from '@/seed/personas';

/**
 * Scorecard math — pure. Per brief §4.1 the tiles are Modules complete,
 * Practice deals closed, Personas mastered.
 */

export interface ScorecardTile {
  label: string;
  value: number;
  total: number;
  sub: string;
  /** Role-color key used by the Dashboard CSS. */
  color: 'platform' | 'win' | 'warmth';
}

export function computeScorecard(learner: LearnerState): ScorecardTile[] {
  const dealsClosed = learner.session_history.filter((s) => s.outcome === 'closed').length;
  const totalAttempts = learner.session_history.length;

  const masteredCount = learner.personas_mastered.length;
  const unlockedCount = learner.personas_unlocked.length;

  return [
    {
      label: 'Modules complete',
      value: learner.modules_complete,
      total: TOTAL_MODULES,
      sub:
        learner.modules_complete === 0
          ? `Module ${learner.current_module} in progress`
          : `Keep moving — ${TOTAL_MODULES - learner.modules_complete} to go`,
      color: 'platform',
    },
    {
      label: 'Practice deals closed',
      value: dealsClosed,
      total: Math.max(totalAttempts, 1),
      sub:
        totalAttempts === 0
          ? 'Your first session is ahead'
          : dealsClosed === 0
            ? `${totalAttempts} attempt${totalAttempts === 1 ? '' : 's'} · none closed yet`
            : `${dealsClosed} of ${totalAttempts} attempts closed`,
      color: 'win',
    },
    {
      label: 'Personas mastered',
      value: masteredCount,
      total: PERSONA_ORDER.length,
      sub:
        masteredCount === 0
          ? `${unlockedCount} unlocked`
          : `${unlockedCount} unlocked · score 85+ to master`,
      color: 'warmth',
    },
  ];
}
